/**
 * 屏幕宽度工具类
 * 实时更新屏幕宽度到CSS自定义属性中，供SCSS使用
 */

class ScreenWidthManager {
  private static instance: ScreenWidthManager;
  private resizeObserver: ResizeObserver | null = null;
  private isInitialized = false;
  private rafId: number | null = null;
  private boundResizeHandler: () => void;
  private styleElement: HTMLStyleElement | null = null;

  private constructor() {
    // 绑定resize处理函数，避免重复创建
    this.boundResizeHandler = this.handleResize.bind(this);
  }

  public static getInstance(): ScreenWidthManager {
    if (!ScreenWidthManager.instance) {
      ScreenWidthManager.instance = new ScreenWidthManager();
    }
    return ScreenWidthManager.instance;
  }

  /**
   * 初始化屏幕宽度监听
   */
  public init(): void {
    if (this.isInitialized) return;

    // 创建动态style标签用于注入CSS变量
    this.styleElement = document.createElement("style");
    this.styleElement.id = "screen-width-variables";
    document.head.appendChild(this.styleElement);

    // 设置初始值
    this.updateScreenWidth();

    // 监听窗口大小变化（使用防抖优化性能）
    window.addEventListener("resize", this.boundResizeHandler);

    // 使用 ResizeObserver 监听更精确的变化
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(this.boundResizeHandler);
      this.resizeObserver.observe(document.documentElement);
    }

    this.isInitialized = true;
  }

  /**
   * 处理窗口大小变化（防抖处理）
   */
  private handleResize(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
    }
    this.rafId = requestAnimationFrame(() => {
      this.updateScreenWidth();
      this.rafId = null;
    });
  }

  /**
   * 更新屏幕宽度到CSS变量（使用style标签，不在HTML标签上显示）
   */
  private updateScreenWidth(): void {
    if (!this.styleElement) return;

    const width = window.innerWidth;
    const height = window.innerHeight;

    const rootFontSize =
      parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;

    this.styleElement.textContent = `:root {
      --screen-width: ${width}px;
      --screen-height: ${height}px;
      --screen-width-rem: ${width / rootFontSize}rem;
      --screen-height-rem: ${height / rootFontSize}rem;
      --is-mobile: ${width <= 768 ? "1" : "0"};
      --is-tablet: ${width > 768 && width <= 1024 ? "1" : "0"};
      --is-desktop: ${width > 1024 ? "1" : "0"};
    }`;
  }

  /**
   * 获取当前屏幕宽度
   */
  public getScreenWidth(): number {
    return window.innerWidth;
  }

  /**
   * 获取当前屏幕高度
   */
  public getScreenHeight(): number {
    return window.innerHeight;
  }

  /**
   * 销毁监听器
   */
  public destroy(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.styleElement && this.styleElement.parentNode) {
      this.styleElement.parentNode.removeChild(this.styleElement);
      this.styleElement = null;
    }
    window.removeEventListener("resize", this.boundResizeHandler);
    this.isInitialized = false;
  }
}

// 导出单例实例
export const screenWidthManager = ScreenWidthManager.getInstance();

// 便捷函数
export const initScreenWidth = () => screenWidthManager.init();
export const getScreenWidth = () => screenWidthManager.getScreenWidth();
export const getScreenHeight = () => screenWidthManager.getScreenHeight();
